// Okno Logy (jádro LogWindow, postup 77): textový výpis logu jednoho dne. Řádek nástrojů: oblast, den (povinně),
// čas od–do (volitelně), hledaný text, Načíst. Server čte z disku po částech – další část se načte při posunu dolů.
// Barevně jen stav (doručeno / odloženo / odmítnuto) a směr rozhovoru SMTP (< klient, > server).
import { registerComponent } from './factory';
import { action, ApiError } from '../api';
import { h } from '../dom';
import { t, tk } from '../i18n';
import { icon } from '../icons';
import { simpleBar } from '../components/chrome';
import { toast } from '../components/toast';

interface Area { id: string; label: string }
interface Res { lines: string[]; next: string | null; scanned: number; day: string }

const tone = (l: string): string => {
  if (/status=sent|login=ok|submit=ok|\b(2\d\d) /.test(l) && !/status=(deferred|bounced)/.test(l)) return /status=sent|login=ok|submit=ok/.test(l) ? 'ok' : '';
  if (/status=deferred|warning:/.test(l)) return 'warn';
  if (/status=bounced|NOQUEUE: reject|reject:|login=fail|=rejected|error:|fatal:/.test(l)) return 'bad';
  return '';
};

registerComponent('log-view', ({ desc, win, wm }) => {
  const p = desc.payload as { areas: Area[]; today: string; max: number };
  const area = h('select', { class: 'input' }, p.areas.map((a) => h('option', { value: a.id }, a.label))) as HTMLSelectElement;
  const day = h('input', { class: 'input', type: 'date', value: p.today, max: p.today, required: true }) as HTMLInputElement;
  const from = h('input', { class: 'input', type: 'time', step: 60 }) as HTMLInputElement;
  const to = h('input', { class: 'input', type: 'time', step: 60 }) as HTMLInputElement;
  const q = h('input', { class: 'input', type: 'search', placeholder: t('core.logs.search_ph'), maxlength: 200 }) as HTMLInputElement;
  const go = h('button', { type: 'button', class: 'btn primary' }, icon('search'), t('core.logs.load'));
  const lines = h('div', { class: 'log-lines', role: 'log', tabindex: 0 });
  const status = h('div', { class: 'log-status muted small' });
  const more = h('button', { type: 'button', class: 'btn sm', hidden: true }, t('core.logs.more'));
  const field = (label: string, el: HTMLElement) => h('label', { class: 'log-field' }, h('span', { class: 'field-label' }, label), el);
  const el = h('div', { class: 'view log-view chrome-bar' },
    h('form', { class: 'log-tools' }, field(t('core.logs.area'), area), field(t('core.logs.day'), day), field(t('core.logs.from'), from), field(t('core.logs.to'), to), field(t('core.logs.search'), q), go),
    lines, h('div', { class: 'log-foot' }, status, more));
  let cursor: string | null = null;
  let count = 0;
  let busy = false;
  let query: Record<string, string> = {};
  const sb = simpleBar(win, wm, () => [{ id: 'refresh', label: t('core.cmd.refresh'), icon: 'refresh', place: 'more' as const, shortcut: 'F5', onClick: () => void load(true) }]);

  async function load(reset: boolean): Promise<void> {
    if (busy) return;
    if (!day.value) { day.reportValidity(); return; }
    if (reset) {
      query = { area: area.value, day: day.value, from: from.value, to: to.value, q: q.value.trim() };
      cursor = null;
      count = 0;
      lines.replaceChildren();
    }
    busy = true;
    status.textContent = t('core.logs.loading');
    try {
      const r = await action<Res>(desc.app, desc.window, 'read', desc.params, { ...query, cursor: cursor ?? '' });
      const frag = document.createDocumentFragment();
      for (const l of r.lines) frag.appendChild(h('div', { class: 'log-line ' + tone(l) }, l));
      lines.appendChild(frag);
      count += r.lines.length;
      cursor = r.next;
      more.hidden = cursor === null;
      status.textContent = count === 0 && cursor === null ? t('core.logs.empty') : t(cursor === null ? 'core.logs.count_end' : 'core.logs.count_more', { n: count.toLocaleString('cs-CZ') });
    } catch (e) {
      status.textContent = '';
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    } finally {
      busy = false;
    }
  }
  el.querySelector('form')?.addEventListener('submit', (e) => { e.preventDefault(); void load(true); });
  go.addEventListener('click', () => void load(true));
  more.addEventListener('click', () => void load(false));
  // další část při posunu ke konci výpisu
  lines.addEventListener('scroll', () => {
    if (cursor !== null && !busy && lines.scrollTop + lines.clientHeight > lines.scrollHeight - 400) void load(false);
  });
  void load(true);
  return { el, refresh: () => void load(true), setMode: () => sb.refresh() };
});
